import React, { useCallback, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { AddAPhoto, CameraAlt, CheckCircle, CloudUpload, DeleteOutline } from '@mui/icons-material';
import {
  deleteInventoryProductPhoto,
  deleteInventoryVariantPhoto,
  uploadInventoryProductPhoto,
  uploadInventoryVariantPhoto,
  type InventoryExtraPhoto,
  type InventoryLegacyPhoto,
  type InventoryVariantPhotoAngle,
} from '../api/inventoryAdmin';
import {
  PHOTO_ANGLE_LABELS,
  PHOTO_EXTRA_ANGLE_LABEL,
  USED_ITEM_ANGLES,
  type PhotoAngle,
} from '../types/photoQueue';

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPT_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export type InventoryPhotosScope = 'product' | 'variant';

export interface InventoryPhotoSlotView {
  angle: string;
  label: string;
  id: number | null;
  url: string | null;
}

export interface InventoryPhotosPanelProps {
  inventoryId: number;
  scope: InventoryPhotosScope;
  requiredSlots: InventoryPhotoSlotView[];
  extraPhotos: InventoryExtraPhoto[];
  legacyPhotos?: InventoryLegacyPhoto[];
  disabled?: boolean;
  disabledMessage?: string;
  sharedProductNote?: string;
  onUpdated: () => void;
}

interface SlotCardProps {
  slot: InventoryPhotoSlotView;
  isUploading: boolean;
  error: string | null;
  disabled: boolean;
  onSelect: () => void;
  onDelete?: () => void;
}

function SlotCard({ slot, isUploading, error, disabled, onSelect, onDelete }: SlotCardProps) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.75,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'text.secondary',
        }}
      >
        {slot.label}
        {slot.url ? (
          <CheckCircle
            sx={{ fontSize: 12, ml: 0.5, color: 'success.main', verticalAlign: 'middle' }}
          />
        ) : null}
      </Typography>
      <Box
        onClick={() => {
          if (!disabled && !isUploading) onSelect();
        }}
        sx={{
          height: 160,
          borderRadius: 2,
          overflow: 'hidden',
          border: slot.url ? '1px solid' : '2px dashed',
          borderColor: error ? 'error.main' : 'divider',
          bgcolor: slot.url ? 'transparent' : 'grey.50',
          cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
          position: 'relative',
          opacity: disabled ? 0.65 : 1,
          transition: 'border-color 0.15s',
          '&:hover': {
            borderColor: !slot.url && !disabled && !isUploading ? 'text.primary' : undefined,
          },
        }}
      >
        {isUploading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.75)',
              zIndex: 2,
            }}
          >
            <CircularProgress size={32} sx={{ color: '#111827' }} />
          </Box>
        ) : null}
        {slot.url ? (
          <>
            <img
              src={slot.url}
              alt={slot.label}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            {!disabled ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'rgba(0,0,0,0.45)',
                  opacity: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'opacity 0.15s',
                  '&:hover': { opacity: 1 },
                }}
              >
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
                >
                  <CloudUpload sx={{ color: 'white', fontSize: 32 }} />
                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 700 }}>
                    Cambiar
                  </Typography>
                </Box>
              </Box>
            ) : null}
            {!disabled && onDelete ? (
              <Tooltip title="Eliminar foto">
                <IconButton
                  size="small"
                  aria-label={`Eliminar ${slot.label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    bgcolor: 'rgba(255,255,255,0.92)',
                    '&:hover': { bgcolor: 'white' },
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </>
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
            }}
          >
            <CameraAlt sx={{ fontSize: 36, color: 'grey.300' }} />
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
              Subir foto
            </Typography>
          </Box>
        )}
      </Box>
      {error ? (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

SlotCard.defaultProps = {
  onDelete: undefined,
};

interface LegacyPhotosGridProps {
  photos: InventoryLegacyPhoto[];
}

function LegacyPhotosGrid({ photos }: LegacyPhotosGridProps) {
  if (photos.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
        Fotos importadas (sin ángulo)
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 0.25, mb: 1 }}
      >
        Solo lectura. Sube de nuevo con un ángulo para reemplazarlas.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 1.5,
        }}
      >
        {photos.map((photo, index) => (
          <Box key={photo.id}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 0.75,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'text.secondary',
              }}
            >
              Importada {index + 1}
            </Typography>
            <Box
              sx={{
                height: 120,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <img
                src={photo.url}
                alt={`Foto importada ${index + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function validateImageFile(file: File): string | null {
  if (!ACCEPT_IMAGE_TYPES.includes(file.type)) {
    return 'Formato no válido. Usa PNG, JPG o WEBP.';
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return 'La imagen no debe superar 5MB.';
  }
  return null;
}

function InventoryPhotosPanel({
  inventoryId,
  scope,
  requiredSlots,
  extraPhotos,
  legacyPhotos = [],
  disabled = false,
  disabledMessage,
  sharedProductNote,
  onUpdated,
}: InventoryPhotosPanelProps): React.ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingRef = useRef<{ angle: string; replaceImageId: number | null } | null>(null);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [slotErrors, setSlotErrors] = useState<Record<string, string>>({});
  const [extraError, setExtraError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const openFilePicker = useCallback((angle: string, replaceImageId: number | null) => {
    pendingRef.current = { angle, replaceImageId };
    fileInputRef.current?.click();
  }, []);

  const uploadPhoto = useCallback(
    async (angle: string, file: File, replaceImageId: number | null) => {
      const validationError = validateImageFile(file);
      if (validationError) return { ok: false as const, error: validationError };

      if (scope === 'product') {
        if (angle !== 'front' && angle !== 'extra') {
          return { ok: false as const, error: 'Ángulo de producto no válido.' };
        }
        return uploadInventoryProductPhoto(inventoryId, angle, file, replaceImageId ?? undefined);
      }

      return uploadInventoryVariantPhoto(
        inventoryId,
        angle as InventoryVariantPhotoAngle,
        file,
        replaceImageId ?? undefined
      );
    },
    [inventoryId, scope]
  );

  const deletePhoto = useCallback(
    async (imageId: number) => {
      if (scope === 'product') {
        return deleteInventoryProductPhoto(inventoryId, imageId);
      }
      return deleteInventoryVariantPhoto(inventoryId, imageId);
    },
    [inventoryId, scope]
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      const pending = pendingRef.current;
      e.target.value = '';
      if (!file || !pending) return;

      const uploadKey = pending.angle === 'extra' ? 'extra-new' : pending.angle;
      setUploadingKey(uploadKey);
      if (pending.angle === 'extra') {
        setExtraError(null);
      } else {
        setSlotErrors((prev) => {
          const next = { ...prev };
          delete next[pending.angle];
          return next;
        });
      }

      const result = await uploadPhoto(pending.angle, file, pending.replaceImageId);
      setUploadingKey(null);
      pendingRef.current = null;

      if (!result.ok) {
        if (pending.angle === 'extra') {
          setExtraError(result.error);
        } else {
          setSlotErrors((prev) => ({ ...prev, [pending.angle]: result.error }));
        }
        return;
      }
      onUpdated();
    },
    [onUpdated, uploadPhoto]
  );

  const handleDelete = useCallback(
    async (imageId: number) => {
      setDeletingId(imageId);
      const result = await deletePhoto(imageId);
      setDeletingId(null);
      if (!result.ok) {
        setExtraError(result.error);
        return;
      }
      setExtraError(null);
      onUpdated();
    },
    [deletePhoto, onUpdated]
  );

  const gridColumns = requiredSlots.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr';

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept="image/png,image/jpeg,image/jpg,image/webp"
        onChange={handleFileChange}
      />

      {disabled && disabledMessage ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {disabledMessage}
        </Alert>
      ) : null}

      {sharedProductNote ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {sharedProductNote}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: gridColumns },
          gap: 1.5,
        }}
      >
        {requiredSlots.map((slot) => (
          <SlotCard
            key={slot.angle}
            slot={slot}
            isUploading={uploadingKey === slot.angle}
            error={slotErrors[slot.angle] ?? null}
            disabled={disabled || deletingId != null}
            onSelect={() => openFilePicker(slot.angle, slot.id)}
            onDelete={
              slot.id != null
                ? () => {
                    handleDelete(slot.id as number).catch(() => {});
                  }
                : undefined
            }
          />
        ))}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
          Fotos adicionales (opcional)
        </Typography>
        <Box
          sx={{
            mt: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 1.5,
          }}
        >
          {extraPhotos.map((photo, index) => (
            <Box key={photo.id}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.75,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                }}
              >
                {PHOTO_EXTRA_ANGLE_LABEL} {index + 1}
              </Typography>
              <Box
                sx={{
                  height: 120,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  position: 'relative',
                }}
              >
                {deletingId === photo.id ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255,255,255,0.75)',
                      zIndex: 2,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : null}
                <img
                  src={photo.url}
                  alt={`${PHOTO_EXTRA_ANGLE_LABEL} ${index + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                {!disabled ? (
                  <Tooltip title="Eliminar foto">
                    <IconButton
                      size="small"
                      aria-label={`Eliminar ${PHOTO_EXTRA_ANGLE_LABEL} ${index + 1}`}
                      disabled={deletingId != null}
                      onClick={() => {
                        handleDelete(photo.id).catch(() => {});
                      }}
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        bgcolor: 'rgba(255,255,255,0.92)',
                        '&:hover': { bgcolor: 'white' },
                      }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
            </Box>
          ))}

          {!disabled ? (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.75,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                }}
              >
                Agregar {PHOTO_EXTRA_ANGLE_LABEL.toLowerCase()}
              </Typography>
              <Box
                onClick={() => {
                  if (!disabled && uploadingKey !== 'extra-new' && deletingId == null) {
                    openFilePicker('extra', null);
                  }
                }}
                sx={{
                  height: 120,
                  borderRadius: 2,
                  border: '2px dashed',
                  borderColor: extraError ? 'error.main' : 'divider',
                  bgcolor: 'grey.50',
                  cursor:
                    disabled || uploadingKey === 'extra-new' || deletingId != null
                      ? 'not-allowed'
                      : 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                {uploadingKey === 'extra-new' ? (
                  <CircularProgress size={28} sx={{ color: '#111827' }} />
                ) : (
                  <>
                    <AddAPhoto sx={{ fontSize: 32, color: 'grey.400' }} />
                    <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
                      Subir foto
                    </Typography>
                  </>
                )}
              </Box>
              {extraError ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {extraError}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>

      <LegacyPhotosGrid photos={legacyPhotos} />
    </Box>
  );
}

export function buildProductPhotoSlots(
  photos: Array<{ id: number; angle: string; url: string }>,
  thumbnailUrl: string | null
): InventoryPhotoSlotView[] {
  const front = photos.find((p) => p.angle === 'front');
  return [
    {
      angle: 'front',
      label: PHOTO_ANGLE_LABELS.front,
      id: front?.id ?? null,
      url: front?.url ?? thumbnailUrl,
    },
  ];
}

export function buildUsedVariantPhotoSlots(
  conditionPhotos: Array<{ id: number | null; angle: string; url: string | null }>
): InventoryPhotoSlotView[] {
  const byAngle = new Map(conditionPhotos.map((p) => [p.angle, p]));
  return USED_ITEM_ANGLES.map((angle) => {
    const slot = byAngle.get(angle);
    return {
      angle,
      label: PHOTO_ANGLE_LABELS[angle as PhotoAngle],
      id: slot?.id ?? null,
      url: slot?.url ?? null,
    };
  });
}

export default InventoryPhotosPanel;

InventoryPhotosPanel.defaultProps = {
  disabled: false,
  disabledMessage: undefined,
  sharedProductNote: undefined,
  legacyPhotos: [],
};
